import { isEqual } from 'lodash';

/**
 * Checks if the execution policy has changed between current and initial blocks.
 * Uses deep equality comparison to handle exec_policy values.
 *
 * Execution policy can be one of:
 * - "wait_result": Execute next action sequentially (wait for completion)
 * - "ignore_result": Execute next action in parallel (don't wait)
 * - "check_result": Execute next action sequentially if this action succeeds
 * - undefined/null: Inherit global setting (default behavior)
 *
 * @param {Object} currentBlock - The current block object that may contain an exec_policy property
 * @param {Object} initialBlock - The initial block object to compare against
 * @returns {boolean} Returns true if exec_policy values are different, false if they are equal
 *
 * @example
 * // Different execution policies
 * const current = { exec_policy: 'wait_result' };
 * const initial = { exec_policy: 'ignore_result' };
 * isExecPolicyChanged(current, initial); // true
 *
 * @example
 * // Same execution policy
 * const current = { exec_policy: 'wait_result' };
 * const initial = { exec_policy: 'wait_result' };
 * isExecPolicyChanged(current, initial); // false
 *
 * @example
 * // From global setting to specific policy
 * const current = { exec_policy: 'wait_result' };
 * const initial = { name: 'block' }; // inherit global (no exec_policy)
 * isExecPolicyChanged(current, initial); // true
 *
 * @example
 * // From specific policy to global setting
 * const current = { name: 'block' }; // inherit global (no exec_policy)
 * const initial = { exec_policy: 'ignore_result' };
 * isExecPolicyChanged(current, initial); // true
 *
 * @example
 * // Both using global settings (inherit)
 * const current = { name: 'block1' }; // no exec_policy property
 * const initial = { name: 'block2' }; // no exec_policy property
 * isExecPolicyChanged(current, initial); // false
 *
 * @example
 * // Handles null/undefined blocks safely
 * isExecPolicyChanged(null, { exec_policy: 'wait_result' }); // true
 * isExecPolicyChanged({ exec_policy: 'check_result' }, undefined); // true
 * isExecPolicyChanged(null, null); // false
 */
export const isExecPolicyChanged = (currentBlock, initialBlock) =>
    !isEqual(currentBlock?.exec_policy, initialBlock?.exec_policy);
